import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { MemoizedGroupedTranscriptEntry } from '@/components/GroupedTranscriptEntry';
import { isTimeInRange } from '@/lib/time';
import { motion } from 'framer-motion';
import { FileText, FileTextIcon, MessageSquare, Sparkles } from 'lucide-react';
import { EmptyState } from '../ui/interactive-empty-state';
import { SpinnerIcon } from '../ui/SpinnerIcon';
import { useI18n } from '@/lib/i18n';
import { useUsage } from '@/contexts/usage-context';
import { UpgradePromptDialog } from '../ui/upgrade-prompt-dialog';
import { useUser } from '@/lib/context/user-context';
import { Skiper70 } from '../skiper70';
import { supabase } from '@/core/supabase';
import { mergeTranscriptSegments } from '@/lib/transcript/segment-merge';
import { toast } from 'sonner';
import type { ActiveVocabularyTermData } from '@/components/episode/ImmersiveVocabularyCards';

// 🔥 Streaming transcript types
interface SubtitleCue {
  text: string;
  timestamp: [number, number];
  translation?: string | Record<string, string>;
  chunks?: Array<{
    text: string;
    timestamp: [number, number];
    translation?: string | Record<string, string>;
  }>;
}

interface TranscriptSectionProps {
  groupedSentences: any[];
  youtubeTime: number;
  ytPlayer: any;
  userSettings: any;
  wordHighlightEnabled: boolean;
  practiceMode: boolean;
  practiceModeType: 'vocabulary' | 'toefl' | 'shadowing';
  vocabularyTerms: string[];
  setIsPlaying: (playing: boolean) => void;
  showTranslation: boolean;
  isWordCheckerReady: boolean;
  isVideoMinimized?: boolean;
  episodeId?: string;
  episodeData?: any; // 🔥 新增：传递完整的episode数据用于恢复
  episodeDuration?: number; // 🔥 新增：直接传递视频时长（秒）
  isImmersiveMode?: boolean;
  activeSubtitleId?: string | null;
  onActiveSubtitleChange?: (
    subtitleId: string | null,
    subtitleText?: string,
    subtitleTranslation?: string,
    subtitleTimestamp?: [number, number]
  ) => void;
  onTranscriptUpdate?: (transcript: any[], audioUrl?: string) => void;
  session?: any;
  generationRecords?: {
    transcript: boolean;
    summary: boolean;
    vocabulary: boolean;
    mindmap: boolean;
    cleanread: boolean;
  };
  hasTwoBrokeGirlsAccess?: boolean;
  isTwoBrokeGirlsEpisode?: boolean;
  // 🔥 新增：是否允许 Basic Plan Lite 用户访问
  allowBasicPlanLite?: boolean;
  // 🎯 Vocabulary 高亮相关 props
  activeVocabularyTerms?: ActiveVocabularyTermData[];
  staticVocabularyTerms?: ActiveVocabularyTermData[];
}

export function TranscriptSection({
  groupedSentences,
  youtubeTime,
  ytPlayer,
  userSettings,
  wordHighlightEnabled,
  practiceMode,
  practiceModeType,
  vocabularyTerms,
  setIsPlaying,
  showTranslation,
  isWordCheckerReady,
  isVideoMinimized = false,
  episodeId,
  episodeData, // 🔥 新增：接收episode数据
  episodeDuration, // 🔥 新增：直接接收视频时长（秒）
  isImmersiveMode = false,
  activeSubtitleId,
  onActiveSubtitleChange,
  onTranscriptUpdate,
  session,
  generationRecords,
  hasTwoBrokeGirlsAccess: hasTwoBrokeGirlsAccessProp = false,
  isTwoBrokeGirlsEpisode = false,
  allowBasicPlanLite = false,
  // 🎯 Vocabulary 高亮
  activeVocabularyTerms,
  staticVocabularyTerms,
}: TranscriptSectionProps) {
  // Usage context for real-time updates
  const { usageInfo, refreshUsage } = useUsage();
  const { user } = useUser();
  const { t } = useI18n();

  const metadataTwoBrokeGirlsAccess = useMemo(() => {
    const flag = user?.user_metadata?.twoBrokeGirlsAccess === true;
    if (!flag) return false;

    const expiresAt = user?.user_metadata?.twoBrokeGirlsAccessExpiresAt;
    if (!expiresAt) return true;

    const expiresDate = new Date(expiresAt);
    if (Number.isNaN(expiresDate.getTime())) {
      return true;
    }

    return expiresDate > new Date();
  }, [user?.user_metadata?.twoBrokeGirlsAccess, user?.user_metadata?.twoBrokeGirlsAccessExpiresAt]);
  const hasTwoBrokeGirlsAccess = useMemo(
    () => (hasTwoBrokeGirlsAccessProp || metadataTwoBrokeGirlsAccess) && isTwoBrokeGirlsEpisode,
    [hasTwoBrokeGirlsAccessProp, metadataTwoBrokeGirlsAccess, isTwoBrokeGirlsEpisode]
  );

  // 🔥 添加本地状态来存储生成的transcript
  const [localTranscript, setLocalTranscript] = useState<any[]>([]);
  
  // 🔥 Toast 队列机制 - 确保每个 progress toast 至少显示一段时间
  const toastQueueRef = useRef<Array<{ message: string; isSuccess: boolean }>>([]);
  const lastToastTimeRef = useRef<number>(0);
  const toastProcessingRef = useRef<boolean>(false);
  const MIN_TOAST_DISPLAY_MS = 600; // 每个 toast 最少显示 600ms
  
  const processToastQueue = useCallback(() => {
    if (toastProcessingRef.current || toastQueueRef.current.length === 0) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastToast = now - lastToastTimeRef.current;
    
    if (timeSinceLastToast < MIN_TOAST_DISPLAY_MS) {
      // 还没到时间，延迟处理
      toastProcessingRef.current = true;
      setTimeout(() => {
        toastProcessingRef.current = false;
        processToastQueue();
      }, MIN_TOAST_DISPLAY_MS - timeSinceLastToast);
      return;
    }
    
    // 取出队列中的下一个 toast（跳过中间的，只显示最新的）
    const nextToast = toastQueueRef.current.pop();
    toastQueueRef.current = []; // 清空队列
    
    if (nextToast) {
      lastToastTimeRef.current = now;
      if (nextToast.isSuccess) {
        toast.success(nextToast.message, { id: 'transcribe-progress' });
      } else {
        toast.loading(nextToast.message, { id: 'transcribe-progress' });
      }
    }
  }, []);
  
  const enqueueToast = useCallback((message: string, isSuccess = false) => {
    // 如果是第一个 toast 或距离上次已经足够久，直接显示
    const now = Date.now();
    if (lastToastTimeRef.current === 0 || now - lastToastTimeRef.current >= MIN_TOAST_DISPLAY_MS) {
      lastToastTimeRef.current = now;
      if (isSuccess) {
        toast.success(message, { id: 'transcribe-progress' });
      } else {
        toast.loading(message, { id: 'transcribe-progress' });
      }
    } else {
      // 加入队列
      toastQueueRef.current.push({ message, isSuccess });
      processToastQueue();
    }
  }, [processToastQueue]);

  // 🔥 新增：跟踪用户是否已经生成了transcript（用于free plan用户）
  const [hasGeneratedTranscript, setHasGeneratedTranscript] = useState(() => {
    // 从localStorage初始化状态
    if (typeof window !== 'undefined' && episodeId) {
      const key = `transcript-generated-${episodeId}`;
      return localStorage.getItem(key) === 'true';
    }
    return false;
  });

  // 🔥 新增：跟踪当前会话中用户是否已通过扣费确认流程
  // 与 GlobalPlayerControls 的 transcriptGenerationConfirmed 保持一致
  const [transcriptConfirmedInSession, setTranscriptConfirmedInSession] = useState(false);
  
  // 🔥 生成页面加载 ID（使用 window 对象，页面刷新后会重置）
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 使用 window 对象存储页面加载 ID，这样刷新页面后会重置
      const win = window as typeof window & { __pageLoadId?: string };
      if (!win.__pageLoadId) {
        win.__pageLoadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      
      // 🔥 检查当前页面加载期间是否已确认（处理 tab 切换时组件重新挂载的情况）
      if (episodeId) {
        const confirmedPageLoadId = sessionStorage.getItem(`session-transcript-confirmed-page-id-${episodeId}`);
        if (confirmedPageLoadId === win.__pageLoadId) {
          setTranscriptConfirmedInSession(true);
        }
      }
    }
  }, [episodeId]);

  // 🔥 Streaming 相关状态（移到 displayTranscript 之前）
  const [streamingSubtitles, setStreamingSubtitles] = useState<SubtitleCue[]>([]);
  const [isStreamingTranscript, setIsStreamingTranscript] = useState(false);
  const streamAbortControllerRef = useRef<AbortController | null>(null);
  const [streamingError, setStreamingError] = useState<string | null>(null);

  // 🔥 当props的groupedSentences更新时，同步更新本地状态
  useEffect(() => {
    if (groupedSentences && groupedSentences.length > 0 && localTranscript.length === 0) {
      setLocalTranscript(groupedSentences);
    }
  }, [groupedSentences, localTranscript.length]);

  // 🔥 合并props和本地状态的transcript
  const displayTranscript = useMemo(() => {
    // 🔥 优先级：streaming subtitles > 本地生成的 transcript > props 传入的
    if (streamingSubtitles && streamingSubtitles.length > 0) {
      // 🔥 对 streaming subtitles 应用 merge 策略，与后端保存到 Redis 时一致
      // 将短句合并成更长的段落（最多 30 个单词），同时保留原始 chunks 用于 word-level 高亮
      const segmentsForMerge = streamingSubtitles.map((cue: any) => ({
        text: cue.text || '',
        translation: cue.translation,
        timestamp: cue.timestamp || [0, 0],
        chunks: cue.chunks || [{
          text: cue.text || '',
          translation: cue.translation,
          timestamp: cue.timestamp || [0, 0],
        }],
        sentences: [],
      }));
      return mergeTranscriptSegments(segmentsForMerge, { maxWordsPerSegment: 30 });
    }
    // 如果本地有生成的transcript，优先使用
    if (localTranscript && localTranscript.length > 0) {
      return localTranscript;
    }
    // 否则使用props传入的
    return groupedSentences || [];
  }, [streamingSubtitles, localTranscript, groupedSentences]);

  const [usageFeedbackState, setUsageFeedbackState] = useState<{
    key: number;
    previous: number;
    current: number;
    limit?: number;
    headline: string;
    subline: string;
  } | null>(null);
  const usageFeedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formatRemainingMessage = useCallback((remaining: number, limit?: number | null) => {
    if (typeof limit === 'number' && limit < 0) {
      return t('transcript.usageRemainingUnlimited');
    }

    const safeLimit = typeof limit === 'number' && limit > 0 ? limit : 3;
    const safeRemaining = Math.max(0, remaining);

    return t('transcript.usageRemaining')
      .replace('{remaining}', String(safeRemaining))
      .replace('{limit}', String(safeLimit));
  }, [t]);

  // 🔍 Debug effect to track generationRecords prop changes
  // useEffect(() => {
  //   console.log('🔄 generationRecords prop changed in TranscriptSection:', {
  //     hasGenerationRecords: !!generationRecords,
  //     generationRecords,
  //     episode: episodeId
  //   });
  // }, [generationRecords, episodeId]);

  useEffect(() => {
    return () => {
      if (usageFeedbackTimeoutRef.current) {
        clearTimeout(usageFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!usageFeedbackState) return;

    if (usageFeedbackTimeoutRef.current) {
      clearTimeout(usageFeedbackTimeoutRef.current);
    }

    usageFeedbackTimeoutRef.current = setTimeout(() => {
      setUsageFeedbackState(null);
    }, 4000);
  }, [usageFeedbackState]);

  useEffect(() => {
    if (!usageFeedbackState) return;

    const latestRemaining = usageInfo?.remainingTranscripts;
    const latestLimit = usageInfo?.transcriptLimit;

    if (typeof latestRemaining !== 'number' || latestRemaining < 0) {
      return;
    }

    if (latestRemaining === usageFeedbackState.previous) {
      return;
    }

    if (
      usageFeedbackState.current !== latestRemaining ||
      usageFeedbackState.limit !== latestLimit
    ) {
      setUsageFeedbackState(prev => prev ? {
        ...prev,
        current: latestRemaining,
        limit: latestLimit,
        subline: formatRemainingMessage(latestRemaining, latestLimit)
      } : prev);
    }
  }, [usageFeedbackState, usageInfo?.remainingTranscripts, usageInfo?.transcriptLimit, formatRemainingMessage]);


  // 🔧 Stable episode metadata
  const episodeMetadata = useMemo(() => {
    // 尝试多种可能的数据结构路径
    const title = episodeData?.content?.title ||
                  episodeData?.title ||
                  episodeData?.episodeTitle ||
                  'Unknown Episode';
    const thumbnail = episodeData?.content?.thumbnail ||
                      episodeData?.thumbnail ||
                      '';

    return {
      title,
      thumbnail
    };
  }, [episodeData]);

  // 🔥 Quota info for EmptyState display
  const episodeDurationSeconds = useMemo(() => {
    // 优先使用直接传入的 episodeDuration prop
    if (typeof episodeDuration === 'number' && episodeDuration > 0) {
      return episodeDuration;
    }
    // 回退到 episodeData 中的 duration
    return episodeData?.content?.duration || 
           episodeData?.duration || 
           episodeData?.content?.content?.duration ||
           0;
  }, [episodeDuration, episodeData]);

  const minutesToDeduct = useMemo(() => {
    if (
      typeof episodeDurationSeconds === "number" &&
      Number.isFinite(episodeDurationSeconds) &&
      episodeDurationSeconds > 0
    ) {
      return Math.ceil(episodeDurationSeconds / 60);
    }
    return 1;
  }, [episodeDurationSeconds]);

  const transcriptMinutesRemaining = usageInfo?.transcriptMinutes?.remaining ?? null;
  const insufficientTranscriptMinutes =
    typeof transcriptMinutesRemaining === "number" &&
    transcriptMinutesRemaining >= 0 &&
    transcriptMinutesRemaining < minutesToDeduct;

  // 🔥 检查是否正在加载时长信息（episodeDurationSeconds 为 0 表示还在加载）
  const isDurationLoading = episodeDurationSeconds === 0;

  // 🔥 检查是否是 Free Plan（使用次数配额而不是时间配额）
  const isFreePlan = usageInfo?.planType === 'free';

  // 🔥 统一的配额不足检查（适用于 Free Plan 的次数配额和其他计划的时间配额）
  const insufficientQuota = isFreePlan 
    ? (usageInfo?.remainingTranscripts ?? 0) <= 0  // Free Plan: 检查次数
    : insufficientTranscriptMinutes;               // 其他: 检查时间

  const quotaInfo = useMemo(() => {
    // 如果用户已经有生成记录，不显示配额信息
    if (generationRecords?.transcript) {
      return null;
    }
    // 如果用户是 premium 且有无限配额，也不显示
    if (usageInfo?.isPremium && (usageInfo?.transcriptMinutes?.limit ?? 0) < 0) {
      return null;
    }
    
    // 🔥 Free Plan 用户使用次数配额
    if (isFreePlan) {
      const remainingCount = usageInfo?.remainingTranscripts ?? 0;
      const totalCount = usageInfo?.transcriptLimit ?? 3;
      return {
        cost: 1, // 每次生成消耗 1 次
        remaining: remainingCount,
        costLabel: t('transcript.dialog.cost', 'Cost'),
        remainingLabel: t('transcript.dialog.remaining', 'Remaining'),
        unitLabel: t('common.count.times', 'times'), // 次数单位
        insufficient: remainingCount <= 0,
        isLoading: false,
      };
    }
    
    // 🔥 其他用户（Basic/Premium）使用时间配额
    return {
      cost: minutesToDeduct,
      remaining: transcriptMinutesRemaining,
      costLabel: t('transcript.dialog.cost', 'Cost'),
      remainingLabel: t('transcript.dialog.remaining', 'Remaining'),
      unitLabel: t('common.time.minutes', 'min'),
      insufficient: insufficientTranscriptMinutes,
      isLoading: isDurationLoading,
    };
  }, [generationRecords?.transcript, usageInfo?.isPremium, usageInfo?.transcriptMinutes?.limit, usageInfo?.remainingTranscripts, usageInfo?.transcriptLimit, isFreePlan, minutesToDeduct, transcriptMinutesRemaining, insufficientTranscriptMinutes, t, isDurationLoading]);
  
  // 🚀 性能优化：提高沉浸模式下的时间精度，避免字幕跳跃
  const throttledTime = useMemo(() => 
    isImmersiveMode 
      ? Math.floor(youtubeTime * 4) / 4  // 沉浸模式下提高精度
      : Math.floor(youtubeTime * 4) / 4  // 正常模式保持原精度
  , [youtubeTime, isImmersiveMode]);
  
  const currentIndex = useMemo(() => {
    return groupedSentences.findIndex((entry) =>
      isTimeInRange(throttledTime, entry.timestamp[0], entry.timestamp[1])
    );
  }, [groupedSentences, throttledTime]);

  const [isGeneratingTranscript, setIsGeneratingTranscript] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // 🔥 合并的生成状态（包含 streaming 和普通生成）
  const isTranscriptLoading = isGeneratingTranscript || isStreamingTranscript;
  const [errorDetails, setErrorDetails] = useState<{
    type: 'limit_exceeded' | 'authentication' | 'general';
    message: string;
    remaining?: number;
    limit?: number;
  } | null>(null);


  // 🔥 获取 YouTube videoId
  const youtubeVideoId = useMemo(() => {
    // 从 episodeData 中提取 videoId
    const videoId = episodeData?.content?.videoId ||
                    episodeData?.videoId ||
                    episodeData?.content?.content?.videoId ||
                    episodeData?.shareId ||
                    episodeId;
    
    // 验证是否像 YouTube videoId（11字符的字母数字字符串）
    if (videoId && typeof videoId === 'string' && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
      return videoId;
    }
    return null;
  }, [episodeData, episodeId]);

  // 🔥 获取原始音频/视频 URL
  const originalAudioUrl = useMemo(() => {
    // 优先使用 audioUrl
    const audioUrl = episodeData?.content?.audioUrl || 
                     episodeData?.audioUrl ||
                     episodeData?.content?.content?.audioUrl;
    if (audioUrl && audioUrl.trim().length > 0) {
      return audioUrl;
    }
    // 回退到 videoUrl
    const videoUrl = episodeData?.content?.videoUrl || 
                     episodeData?.videoUrl ||
                     episodeData?.content?.content?.videoUrl;
    return videoUrl || "";
  }, [episodeData]);

  // 🔥 现在不再需要前端获取 YouTube 音频 URL
  // /api/transcribe-stream 支持直接传入 YouTube video ID，后端会自己获取音频
  // 这样可以避免音频 URL 在前端和后端之间传递时过期的问题

  // 🔥 获取 episode 元数据用于 streaming
  const episodeLanguage = useMemo(() => {
    return episodeData?.content?.content?.language ||
           episodeData?.content?.language ||
           episodeData?.language ||
           undefined;
  }, [episodeData]);

  const episodeTitle = useMemo(() => {
    return episodeData?.content?.title ||
           episodeData?.title ||
           'Unknown Episode';
  }, [episodeData]);

  const episodeAuthor = useMemo(() => {
    return episodeData?.content?.author ||
           episodeData?.author ||
           'Unknown Author';
  }, [episodeData]);

  // 🔥 新增：带重试的 fetch 函数
  const fetchWithRetry = useCallback(async (url: string, options: RequestInit, maxRetries = 1): Promise<Response> => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // 获取最新的 session（可能已经刷新过）
        const currentSession = session;

        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            ...(currentSession?.access_token && {
              'Authorization': `Bearer ${currentSession.access_token}`
            }),
          }
        });

        // 如果是 401 且还有重试机会，尝试刷新页面获取新 token
        if (response.status === 401 && attempt < maxRetries) {
          console.log(`🔄 Received 401, attempting retry ${attempt + 1}/${maxRetries}`);

          // 等待一小段时间，让可能的 token 刷新完成
          await new Promise(resolve => setTimeout(resolve, 1000));

          // 触发页面状态刷新（让父组件重新获取session）
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('auth-refresh'));
          }

          continue; // 重试
        }

        return response;
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        // 等待后重试
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    throw new Error('Max retries exceeded');
  }, [session]);

  // 🔥 从 stream chunk 构建字幕数据
  // 与 GlobalPlayerControls 保持一致：每个 segment 转换为一个独立的 SubtitleCue
  // TranscriptSection/GroupedTranscriptEntry 期望：{ text, timestamp: [start, end], translation, chunks }
  const buildSubtitlesFromStreamChunk = useCallback((payload: any): SubtitleCue[] => {
    if (!payload) {
      return [];
    }

    const chunkIndex = typeof payload.chunkIndex === "number" ? payload.chunkIndex : 0;
    const chunkStart = typeof payload.chunkStart === "number" ? payload.chunkStart : 0;
    const fallbackEnd =
      typeof payload.chunkEnd === "number" && Number.isFinite(payload.chunkEnd)
        ? payload.chunkEnd
        : chunkStart + 0.6;

    const segments = Array.isArray(payload.segments) ? payload.segments : [];
    
    // 如果没有 segments，尝试用 payload.text 构建
    const baseSegments =
      segments.length > 0
        ? segments
        : typeof payload.text === "string" && payload.text.trim().length > 0
          ? [{ text: payload.text, start: chunkStart, end: fallbackEnd, translation: payload.translation }]
          : [];

    // 🔥 关键修改：每个 segment 转换为一个独立的字幕条目（与 GlobalPlayerControls 一致）
    return baseSegments
      .map((segment: any, segmentIndex: number): SubtitleCue | null => {
        if (!segment) {
          return null;
        }

        const text =
          typeof segment.text === "string" && segment.text.trim().length > 0
            ? segment.text.trim()
            : "";
        if (!text) {
          return null;
        }

        const start =
          typeof segment.start === "number" && Number.isFinite(segment.start)
            ? segment.start
            : chunkStart;
        const rawEnd =
          typeof segment.end === "number" && Number.isFinite(segment.end)
            ? segment.end
            : fallbackEnd;
        const end = rawEnd > start ? rawEnd : start + 0.5;
        
        const translation =
          (typeof segment.translation === "string" && segment.translation.trim().length > 0) ||
          (typeof segment.translation === "object" && segment.translation !== null)
            ? segment.translation
            : undefined;

        // 返回 TranscriptSection 期望的格式
        return {
          text,
          timestamp: [start, end] as [number, number],
          translation,
          // chunks 用于 word-level 高亮，这里简单地把自己作为唯一的 chunk
          chunks: [{
            text,
            timestamp: [start, end] as [number, number],
            translation,
          }],
        };
      })
      .filter((cue: SubtitleCue | null): cue is SubtitleCue => Boolean(cue));
  }, []);

  // 🔥 处理 SSE streaming 事件（与 GlobalPlayerControls 一致）
  const handleStreamEvent = useCallback((rawEvent: string) => {
    if (!rawEvent) return;

    const lines = rawEvent.split("\n");
    let eventType = "message";
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5));
      }
    }

    // 🔍 Debug: Log all received events
    console.log("[TranscriptSection] 🔍 SSE event received:", { 
      eventType, 
      hasData: dataLines.length > 0,
    });

    if (!dataLines.length) return;

    let payload: any;
    try {
      payload = JSON.parse(dataLines.join("\n"));
    } catch (error) {
      console.error("[TranscriptStream] Failed to parse SSE payload", error);
      return;
    }

    // 🔔 Handle progress events - show toast notifications
    if (eventType === "progress") {
      const { step, message, percent, currentChunk, totalChunks } = payload;
      console.log("[TranscriptSection] 📢 Progress event:", { step, message, percent });
      
      // 🔥 使用队列机制确保每个 toast 至少显示一段时间
      if (step === 'audio_downloaded') {
        enqueueToast(message || 'Audio downloaded...');
      } else if (step === 'uploading') {
        enqueueToast(message || 'Uploading audio...');
      } else if (step === 'converting') {
        enqueueToast(message || 'Converting audio format...');
      } else if (step === 'transcribing') {
        enqueueToast(message || 'Starting transcription...');
      } else if (step === 'chunk_done' && currentChunk && totalChunks) {
        enqueueToast(`Transcribing... ${currentChunk}/${totalChunks} (${percent}%)`);
      } else if (step === 'vocabulary') {
        enqueueToast(message || 'Generating vocabulary...');
      } else if (step === 'saving') {
        enqueueToast(message || 'Saving transcript...');
      } else if (step === 'complete') {
        enqueueToast('Transcription complete! 🎉', true);
      } else {
        enqueueToast(message || `Processing... ${percent || 0}%`);
      }
      return;
    }

    // 🔥 处理 chunk 事件
    if (eventType === "chunk") {
      const cues = buildSubtitlesFromStreamChunk(payload);
      if (!cues.length) {
        return;
      }
      
      console.log("[TranscriptStream] Received chunk:", {
        chunkIndex: payload.chunkIndex,
        cuesCount: cues.length,
        firstCueText: cues[0]?.text?.slice(0, 50),
      });
      
      setStreamingSubtitles((previous) => {
        const merged = [...previous];
        cues.forEach((cue) => {
          // 🔥 与 GlobalPlayerControls 一致：使用更精确的去重（0.02秒 + 文本匹配）
          const existingIndex = merged.findIndex(
            (existing) =>
              Math.abs(existing.timestamp[0] - cue.timestamp[0]) < 0.02 &&
              existing.text === cue.text
          );
          if (existingIndex === -1) {
            merged.push(cue);
          } else {
            // 更新已存在的条目（可能有新的翻译等）
            merged[existingIndex] = cue;
          }
        });
        merged.sort((a, b) => a.timestamp[0] - b.timestamp[0]);
        return merged;
      });
    } else if (eventType === "done") {
      console.log("[TranscriptStream] Stream completed, total entries:", streamingSubtitles.length);
    } else if (eventType === "error") {
      console.error("[TranscriptStream] Error:", payload);
      setStreamingError(payload?.message || "Transcription failed");
    } else if (eventType === "vocabulary") {
      // 🔥 发出自定义事件，让 GlobalPlayerControls 处理 vocabulary
      console.log("[TranscriptSection] 📢 Vocabulary event received, dispatching to GlobalPlayerControls");
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('transcript-stream-vocabulary', { 
          detail: payload 
        }));
      }
    } else if (eventType === "chapters") {
      // 🔥 发出自定义事件，让 GlobalPlayerControls 处理 chapters
      console.log("[TranscriptSection] 📢 Chapters event received, dispatching to GlobalPlayerControls");
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('transcript-stream-chapters', { 
          detail: payload 
        }));
      }
    }
  }, [buildSubtitlesFromStreamChunk, streamingSubtitles.length]);

  // 🔥 开始 streaming transcript（与 GlobalPlayerControls 一致）
  const startTranscriptStream = useCallback(async () => {
    console.log("[TranscriptSection] 🎬 startTranscriptStream called!");
    
    // 🔥 对于 YouTube 视频，优先使用 video ID（让后端获取最新的音频 URL，避免过期问题）
    // 对于非 YouTube 视频，使用原始 audio URL
    // /api/transcribe-stream 现在支持直接传入 YouTube video ID
    let audioSource: string | null = null;
    
    if (youtubeVideoId) {
      // 🔥 YouTube 视频：直接传 video ID，让后端获取音频 URL（更可靠）
      audioSource = youtubeVideoId;
      console.log('[TranscriptSection] Using YouTube video ID for streaming:', youtubeVideoId);
    } else if (originalAudioUrl) {
      // 非 YouTube 视频：使用原始 audio URL
      audioSource = originalAudioUrl;
      console.log('[TranscriptSection] Using original audio URL for streaming');
    }
    
    if (!audioSource) {
      console.log('[TranscriptSection] No audio source available for streaming');
      return false;
    }

    // 中止之前的 streaming
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;
    }

    const controller = new AbortController();
    streamAbortControllerRef.current = controller;
    setIsStreamingTranscript(true);
    setStreamingSubtitles([]);
    setStreamingError(null);

    const formData = new FormData();
    // 🔥 传递 audio source（可以是 URL 或 YouTube video ID）
    formData.append("audio", audioSource);
    console.log('[TranscriptSection] Streaming with audio source:', audioSource.slice(0, 50) + '...');
    if (episodeId) {
      formData.append("episodeId", episodeId);
    }
    if (episodeLanguage) {
      formData.append("episodeLanguage", episodeLanguage);
    }
    if (episodeTitle) {
      formData.append("episodeTitle", episodeTitle);
    }
    if (episodeAuthor) {
      formData.append("episodeAuthor", episodeAuthor);
    }
    if (episodeDurationSeconds) {
      formData.append("episodeDurationSeconds", String(episodeDurationSeconds));
    }

    try {
      let authHeaders: HeadersInit | undefined;
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        if (authSession?.access_token) {
          authHeaders = { Authorization: `Bearer ${authSession.access_token}` };
        }
      } catch (error) {
        console.warn("[TranscriptStream] Failed to resolve auth session", error);
      }

      const response = await fetch("/api/transcribe-stream", {
        method: "POST",
        headers: authHeaders,
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Transcription failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let chunkCount = 0;

      console.log("[TranscriptSection] 🚀 Starting to read SSE stream");

      while (true) {
        const { value, done } = await reader.read();
        if (value) {
          chunkCount++;
          const decoded = decoder.decode(value, { stream: !done });
          buffer += decoded;
          
          // 🔍 Debug: Log first few chunks
          if (chunkCount <= 5) {
            console.log("[TranscriptSection] 📦 Received chunk", { 
              chunkCount, 
              bytes: value.length,
              preview: decoded.slice(0, 150),
            });
          }
          
          let boundaryIndex: number;
          while ((boundaryIndex = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, boundaryIndex).trim();
            buffer = buffer.slice(boundaryIndex + 2);
            if (rawEvent) {
              handleStreamEvent(rawEvent);
            }
          }
        }
        if (done) {
          console.log("[TranscriptSection] ✅ SSE stream complete", { totalChunks: chunkCount });
          break;
        }
      }

      if (buffer.trim()) {
        handleStreamEvent(buffer.trim());
      }
      
      return true;
    } catch (error) {
      if (controller.signal.aborted) {
        return false;
      }
      console.error("[TranscriptStream] Failed to stream transcription", error);
      setStreamingError(
        error instanceof Error ? error.message : "Transcription stream failed."
      );
      return false;
    } finally {
      if (streamAbortControllerRef.current === controller) {
        streamAbortControllerRef.current = null;
      }
      setIsStreamingTranscript(false);
    }
  }, [
    originalAudioUrl,
    youtubeVideoId,
    episodeId,
    episodeLanguage,
    episodeTitle,
    episodeAuthor,
    episodeDurationSeconds,
    handleStreamEvent,
  ]);

  // 🔥 当 streaming 完成时，通知父组件更新 transcript
  useEffect(() => {
    // 只有当 streaming 刚刚结束（isStreamingTranscript 从 true 变为 false）
    // 并且有 streaming 数据时，才通知父组件
    if (!isStreamingTranscript && streamingSubtitles.length > 0 && onTranscriptUpdate) {
      console.log('[TranscriptSection] Streaming finished, notifying parent with', streamingSubtitles.length, 'subtitles');
      onTranscriptUpdate(streamingSubtitles);
    }
  }, [isStreamingTranscript, streamingSubtitles, onTranscriptUpdate]);

  // 🔥 清理 streaming
  useEffect(() => {
    return () => {
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort();
        streamAbortControllerRef.current = null;
      }
    };
  }, []);

  // Handle transcript generation
  const handleGenerateTranscript = useCallback(async () => {
    try {
      if (!episodeId) {
        console.error('Episode ID is required for transcript generation');
        return;
      }

      // 🔥 统一扣除分钟的条件，与 GlobalPlayerControls 保持一致：
      // 只有当用户没有生成记录 **并且** 已经有现存的字幕数据时才扣除
      // 这样可以避免在真正 streaming 生成新 transcript 时重复扣除
      const hasExistingTranscript = displayTranscript.length > 0 || groupedSentences.length > 0;
      const shouldDeductMinutes = !generationRecords?.transcript && hasExistingTranscript;
      
      console.log('🔍 [TranscriptSection] Deduct minutes check:', {
        hasGenerationRecord: generationRecords?.transcript,
        hasExistingTranscript,
        displayTranscriptLength: displayTranscript.length,
        groupedSentencesLength: groupedSentences.length,
        shouldDeductMinutes,
      });
      
      if (shouldDeductMinutes) {
        // 检查配额是否足够（Free Plan 检查次数，其他检查时间）
        if (insufficientQuota) {
          setErrorDetails({
            type: 'limit_exceeded',
            message: isFreePlan 
              ? t('transcript.quotaExceededCount', 'No remaining transcript generations')
              : t('transcript.quotaExceeded', 'Insufficient transcript minutes quota'),
            remaining: isFreePlan 
              ? (usageInfo?.remainingTranscripts ?? 0)
              : (transcriptMinutesRemaining ?? 0),
            limit: isFreePlan 
              ? (usageInfo?.transcriptLimit ?? 3)
              : (usageInfo?.transcriptMinutes?.limit ?? 0)
          });
          setShowUpgradeDialog(true);
          return;
        }

        // 🔥 开始 loading 状态（与 GlobalPlayerControls 一致）
        setIsGeneratingTranscript(true);

        // 调用 /api/transcript/minutes 扣除分钟数
        try {
          const deductResponse = await fetchWithRetry('/api/transcript/minutes', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              durationSeconds: episodeDurationSeconds || 60,
              shareId: episodeId,
            }),
          });

          if (!deductResponse.ok) {
            const errorData = await deductResponse.json();
            setErrorDetails({
              type: 'limit_exceeded',
              message: errorData.error || t('transcript.quotaExceeded', 'Transcript minutes quota exceeded'),
              remaining: errorData.remaining,
              limit: errorData.limit
            });
            setShowUpgradeDialog(true);
            setIsGeneratingTranscript(false);
            return;
          }

          console.log('✅ Minutes deducted successfully for episode:', episodeId);
        } catch (deductError) {
          console.error('Failed to deduct minutes:', deductError);
          // 继续尝试生成，让后端处理
        }

        // 🔥 立即显示 toast，让用户知道已开始处理
        toast.loading('Preparing transcription...', { id: 'transcribe-progress' });
        
        // 🔥 减少延迟从 3000ms 到 500ms（与 GlobalPlayerControls 一致）
        console.log('⏳ [TranscriptSection] Starting 500ms loading for existing content...');
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('✅ [TranscriptSection] Loading complete');

        // 🔥 标记当前会话中已确认生成（与 GlobalPlayerControls 的 transcriptGenerationConfirmed 一致）
        // 这会让 shouldShowTranscript() 返回 true，从而显示内容
        setTranscriptConfirmedInSession(true);
        
        // 同时也更新 localStorage（用于其他组件）
        setHasGeneratedTranscript(true);
        if (typeof window !== 'undefined' && episodeId) {
          const key = `transcript-generated-${episodeId}`;
          localStorage.setItem(key, 'true');
          // 🔥 同时在 sessionStorage 中设置标记和页面加载 ID，让 VocabularySection 等其他组件可以访问
          sessionStorage.setItem(`session-transcript-confirmed-${episodeId}`, 'true');
          const win = window as typeof window & { __pageLoadId?: string };
          if (win.__pageLoadId) {
            sessionStorage.setItem(`session-transcript-confirmed-page-id-${episodeId}`, win.__pageLoadId);
          }
          // 🔥 触发自定义事件通知其他组件
          window.dispatchEvent(new Event('transcript-confirmed'));
        }
        
        // 刷新 usage
        setTimeout(() => {
          refreshUsage();
        }, 1000);
        
        setIsGeneratingTranscript(false);
        return;
      }

      setIsGeneratingTranscript(true);

      // 🔥 判断是否使用 streaming（与 GlobalPlayerControls 一致）
      // 条件：有 audio URL 或 YouTube video ID，且没有现存 transcript 时使用 streaming
      // /api/transcribe-stream 现在支持直接传入 YouTube video ID
      const hasAudioSource = youtubeVideoId || originalAudioUrl;
      const shouldUseStreaming = hasAudioSource && !hasExistingTranscript;
      
      console.log('🔍 [TranscriptSection] Streaming decision:', {
        youtubeVideoId: !!youtubeVideoId,
        originalAudioUrl: !!originalAudioUrl,
        audioSource: youtubeVideoId ? 'youtubeVideoId (preferred)' : (originalAudioUrl ? 'originalAudioUrl' : 'none'),
        hasExistingTranscript,
        shouldUseStreaming,
      });

      if (shouldUseStreaming) {
        // 🔥 使用 streaming API（与 GlobalPlayerControls 一致）
        console.log('🚀 [TranscriptSection] Starting streaming transcript...');
        const streamingSuccess = await startTranscriptStream();
        
        if (streamingSuccess) {
          // Streaming 成功，标记已生成
          setTranscriptConfirmedInSession(true); // 🔥 修复：设置会话确认状态
          setHasGeneratedTranscript(true);
          if (typeof window !== 'undefined' && episodeId) {
            const key = `transcript-generated-${episodeId}`;
            localStorage.setItem(key, 'true');
            sessionStorage.setItem(`session-transcript-confirmed-${episodeId}`, 'true');
            const win = window as typeof window & { __pageLoadId?: string };
            if (win.__pageLoadId) {
              sessionStorage.setItem(`session-transcript-confirmed-page-id-${episodeId}`, win.__pageLoadId);
            }
            window.dispatchEvent(new Event('transcript-confirmed'));
          }
          
          // 刷新 usage
          setTimeout(() => {
            refreshUsage();
          }, 1000);
          return;
        } else {
          // Streaming 失败，回退到 generate-transcript API
          console.log('⚠️ [TranscriptSection] Streaming failed, falling back to generate-transcript API');
        }
      }

      // 使用 generate-transcript API（YouTube 视频或 streaming 失败的回退）
      const response = await fetchWithRetry('/api/generate-transcript', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          episodeId,
          episodeData // 🔥 新增：传递episode数据用于恢复
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // 处理不同类型的错误
        setUsageFeedbackState(null);
        
        if (response.status === 429) {
          // 额度已用完
          setErrorDetails({
            type: 'limit_exceeded',
            message: errorData.error,
            remaining: errorData.remaining,
            limit: errorData.limit
          });
          setShowUpgradeDialog(true);
          return;
        } else if (response.status === 401) {
          setErrorDetails({
            type: 'authentication',
            message: 'Authentication required. Please log in or refresh the page to continue.'
          });
          setShowUpgradeDialog(true);
          return;
        } else {
          setErrorDetails({
            type: 'general',
            message: errorData.error || 'Failed to generate transcript'
          });
          setShowUpgradeDialog(true);
          return;
        }
      }

      const result = await response.json();
      
      // console.log('🔍 TranscriptSection received result:', {
      //   success: result.success,
      //   hasTranscript: !!result.transcript,
      //   transcriptLength: result.transcript?.length,
      //   message: result.message
      // });
      
      if (result.success && result.transcript) {
        // 🔥 立即更新本地状态以显示transcript
        setLocalTranscript(result.transcript);

        // 🔥 标记已经生成了transcript
        setTranscriptConfirmedInSession(true); // 🔥 修复：设置会话确认状态
        setHasGeneratedTranscript(true);
        // 保存到localStorage以便组件重新渲染后保持状态
        if (typeof window !== 'undefined' && episodeId) {
          const key = `transcript-generated-${episodeId}`;
          localStorage.setItem(key, 'true');
          sessionStorage.setItem(`session-transcript-confirmed-${episodeId}`, 'true');
          const win = window as typeof window & { __pageLoadId?: string };
          if (win.__pageLoadId) {
            sessionStorage.setItem(`session-transcript-confirmed-page-id-${episodeId}`, win.__pageLoadId);
          }
          window.dispatchEvent(new Event('transcript-confirmed'));
        }

        // Notify parent component about the update, including audioUrl if present
        if (onTranscriptUpdate) {
          onTranscriptUpdate(result.transcript, result.audioUrl);
        }

        // 🔥 刷新usage状态以获取最新的剩余配额
        setTimeout(() => {
          refreshUsage();
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to generate transcript');
      }
    } catch (error) {
      console.error('Error generating transcript:', error);
      
      // 显示通用错误提示dialog
      setErrorDetails({
        type: 'general',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      setShowUpgradeDialog(true);
    } finally {
      setIsGeneratingTranscript(false);
    }
  }, [
    episodeId,
    onTranscriptUpdate,
    refreshUsage,
    fetchWithRetry,
    generationRecords?.transcript,
    insufficientTranscriptMinutes,
    transcriptMinutesRemaining,
    usageInfo?.transcriptMinutes?.limit,
    episodeDurationSeconds,
    t,
    // 🔥 Streaming 相关依赖
    originalAudioUrl,
    youtubeVideoId,
    startTranscriptStream,
    displayTranscript.length,
    groupedSentences.length,
  ]);

  // Find current active chunk within the current group
  const currentChunk = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < groupedSentences.length) {
      const currentGroup = groupedSentences[currentIndex];
      if (currentGroup.chunks && currentGroup.chunks.length > 0) {
        return currentGroup.chunks.find((chunk: any) =>
          isTimeInRange(throttledTime, chunk.timestamp[0], chunk.timestamp[1])
        );
      }
    }
    return null;
  }, [groupedSentences, currentIndex, throttledTime]);

  // 🚀 性能优化：缓存字幕 ID 生成
  const subtitleIdCache = useRef(new Map<string, string>());
  const generateSubtitleId = useCallback((entry: any) => {
    const key = `${entry.timestamp[0]}-${entry.timestamp[1]}`;
    if (!subtitleIdCache.current.has(key)) {
      subtitleIdCache.current.set(key, `subtitle-${key}`);
    }
    return subtitleIdCache.current.get(key)!;
  }, []);

  // Report active subtitle changes for immersive mode (use chunk-level subtitles)
  useEffect(() => {
    if (isImmersiveMode && onActiveSubtitleChange) {
      // 🚀 优化：在时间间隙期间清除字幕，防止显示错误内容
      if (currentIndex === -1 || currentIndex >= groupedSentences.length) {
        onActiveSubtitleChange(null, '', '');
        return;
      }
      
      // 优先使用chunk级别的字幕
      if (currentChunk) {
        const subtitleId = generateSubtitleId(currentChunk);
        const subtitleText = currentChunk.text?.trim() || '';
        const subtitleTranslation = currentChunk.translation?.trim() || '';
        
        // 只有在有实际内容时才更新字幕
        if (subtitleText || subtitleTranslation) {
          onActiveSubtitleChange(
            subtitleId,
            subtitleText,
            subtitleTranslation,
            currentChunk.timestamp as [number, number]
          );
        } else {
          onActiveSubtitleChange(null, '', '');
        }
      } else {
        // 🚀 修复：当没有匹配的chunk时，直接清除字幕，不使用Group字幕
        onActiveSubtitleChange(null, '', '');
      }
    }
  }, [currentIndex, currentChunk, groupedSentences, isImmersiveMode, onActiveSubtitleChange, generateSubtitleId]);

  // Remove debounced currentIndex as it's not necessary with throttling
  
  // Stable onSeek callback with better caching
  const handleSeek = useCallback((time: number) => {
    if (ytPlayer?.seekTo) {
      ytPlayer.seekTo(time);
    }
    
    // Report subtitle change for immersive mode when seeking (use chunk-level)
    if (isImmersiveMode && onActiveSubtitleChange) {
      // First try to find chunk-level match
      let foundChunk = null;
      for (const group of groupedSentences) {
        if (group.chunks && group.chunks.length > 0) {
          foundChunk = group.chunks.find((chunk: any) =>
            isTimeInRange(time, chunk.timestamp[0], chunk.timestamp[1])
          );
          if (foundChunk) break;
        }
      }
      
      if (foundChunk) {
        const subtitleId = generateSubtitleId(foundChunk);
        const subtitleText = foundChunk.text?.trim() || '';
        const subtitleTranslation = foundChunk.translation?.trim() || '';
        
        // 只有在有实际内容时才更新字幕
        if (subtitleText || subtitleTranslation) {
          onActiveSubtitleChange(
            subtitleId,
            subtitleText,
            subtitleTranslation,
            foundChunk.timestamp as [number, number]
          );
        } else {
          onActiveSubtitleChange(null, '', '');
        }
      } else {
        // 🚀 修复：跳转时如果没有找到chunk，直接清除字幕，不使用Group字幕
        onActiveSubtitleChange(null, '', '');
      }
    }
  }, [ytPlayer, isImmersiveMode, onActiveSubtitleChange, groupedSentences, generateSubtitleId]);

  // Memoize vocabulary terms to prevent unnecessary re-renders
  const stableVocabularyTerms = useMemo(() => vocabularyTerms, [vocabularyTerms]);

  // 🔥 检查用户是否是 Basic Plan Lite
  const isBasicPlanLite = useMemo(() => {
    const planType = usageInfo?.planType;
    return planType === 'basic_lite' || usageInfo?.isBasicPlanLite === true;
  }, [usageInfo?.planType, usageInfo?.isBasicPlanLite]);

  // 🔥 检查是否应该显示内容（生成记录检查）
  // 与 GlobalPlayerControls 保持一致：优先检查 Redis 中的 generationRecords
  const shouldShowTranscript = useCallback(() => {
    const isBasicPlanLite = usageInfo?.planType === 'basic_lite' || user?.user_metadata?.isBasicPlanLite === true;
    const isPremium = usageInfo?.isPremium === true || user?.user_metadata?.isPremium === true;
    const restrictToSeries = metadataTwoBrokeGirlsAccess && !isTwoBrokeGirlsEpisode && !isBasicPlanLite && !isPremium;

    if (restrictToSeries) {
      return false;
    }

    // 🔥 优先检查：如果有数据且正在 streaming，立即显示（与 GlobalPlayerControls 一致）
    if (isStreamingTranscript && streamingSubtitles.length > 0) {
      return true;
    }

    // 🎯 allowBasicPlanLite=true 视频：对所有付费用户（Basic Plan Lite / Basic / Premium）自动确认
    // 不显示 "Generate Transcript" 按钮，直接显示内容
    if (allowBasicPlanLite && displayTranscript && displayTranscript.length > 0) {
      console.log('🔍 [shouldShowTranscript] allowBasicPlanLite=true with content, auto-confirming');
      return true;
    }

    // 🎯 hasTwoBrokeGirlsAccess 用户：直接检查内容是否存在
    if (hasTwoBrokeGirlsAccess && displayTranscript && displayTranscript.length > 0) {
      return true;
    }

    // 🔥 优先检查：如果当前会话中已确认生成（通过点击 Generate 按钮并完成 3 秒 loading），
    // 直接显示内容，不再检查 Redis 的 generationRecords
    // 这确保用户点击 Generate 后能立即看到内容
    if (transcriptConfirmedInSession && displayTranscript && displayTranscript.length > 0) {
      console.log('🔍 [shouldShowTranscript] Confirmed in current session, showing content');
      return true;
    }
    
    // 如果没有 generationRecords 对象，说明 API 还没返回或用户未登录
    if (!generationRecords) {
      // 🔥 allowBasicPlanLite=true 时，即使没有 generationRecords，也尝试显示内容
      if (allowBasicPlanLite && displayTranscript && displayTranscript.length > 0) {
        console.log('🔍 [shouldShowTranscript] allowBasicPlanLite=true, no generationRecords but has content');
        return true;
      }
      console.log('🔍 [shouldShowTranscript] No generationRecords, showing Generate button');
      return false;
    }
    
    // 🔥 关键检查：如果 Redis 中 generationRecords.transcript === false，
    // 且用户在当前会话中没有点击过 Generate，显示 "Generate Transcript" 按钮
    // 但如果 allowBasicPlanLite=true，即使没有生成记录，也自动确认显示内容
    if (!generationRecords.transcript) {
      // 🎯 allowBasicPlanLite=true 时自动确认，不显示 Generate 按钮
      if (allowBasicPlanLite && displayTranscript && displayTranscript.length > 0) {
        console.log('🔍 [shouldShowTranscript] allowBasicPlanLite=true, no generation record but has content, auto-confirming');
        return true;
      }
      console.log('🔍 [shouldShowTranscript] No transcript generation record in Redis, showing Generate button', {
        hasLocalStorageRecord: hasGeneratedTranscript,
        hasContent: displayTranscript?.length > 0,
        allowBasicPlanLite,
      });
      return false;
    }

    // 🔥 只有当 Redis 记录确认用户已生成过，才显示内容
    // 如果正在生成中或有本地数据，也允许显示
    if (isTranscriptLoading && displayTranscript && displayTranscript.length > 0) {
      return true;
    }

    // 🔥 如果本地有生成的 transcript（localTranscript），立即显示
    if (localTranscript && localTranscript.length > 0) {
      return true;
    }

    // 根据 Redis 生成记录决定是否显示
    return generationRecords.transcript;
  }, [
    generationRecords,
    displayTranscript,
    localTranscript,
    allowBasicPlanLite,
    usageInfo?.planType,
    usageInfo?.isPremium,
    user?.user_metadata?.isBasicPlanLite,
    user?.user_metadata?.isPremium,
    hasTwoBrokeGirlsAccess,
    hasGeneratedTranscript,
    isTwoBrokeGirlsEpisode,
    isStreamingTranscript,
    streamingSubtitles.length,
    isTranscriptLoading,
    transcriptConfirmedInSession,
  ]);

  // 🔥 检查是否应该显示升级 dialog
  // Free Plan 用户：有 3 次生成额度，显示 "Generate" 按钮（不显示升级 dialog）
  // Basic Plan Lite 用户：如果 allowBasicPlanLite 为 false，显示升级 dialog
  const shouldShowUpgradeDialog = useMemo(() => {
    // 如果有生成记录，不显示升级 dialog
    if (generationRecords?.transcript) return false;
    // 如果当前会话已确认，不显示升级 dialog
    if (transcriptConfirmedInSession) return false;
    
    // Basic Plan Lite 用户：检查 allowBasicPlanLite 字段
    if (isBasicPlanLite && !allowBasicPlanLite) return true;
    
    return false;
  }, [isBasicPlanLite, allowBasicPlanLite, generationRecords?.transcript, transcriptConfirmedInSession]);

  // Handle empty transcript state or no generation record
  if (!shouldShowTranscript()) {
    // 🔥 Free Plan 用户或不允许访问的 Basic Plan Lite 用户显示升级 dialog
    if (shouldShowUpgradeDialog) {
      return (
        <div className={`transcript-content-container ${
          isImmersiveMode 
            ? "bg-transparent border-none" 
            : "mt-1 bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50"
        }`}>
          {/* Hide header in immersive mode */}
          {!isImmersiveMode && (
            <div className="flex items-center justify-between mb-6 p-2 sm:p-4 border-b border-border/50">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-6 bg-red-500 rounded-full"></div>
                <h3 className="text-xl font-bold text-foreground">{t('transcript.title')}</h3>
              </div>
            </div>
          )}
          {/* 🔥 Free Plan 用户：显示升级提示 */}
          <UpgradePromptDialog
            isOpen={true}
            onClose={() => {
              // 关闭时可以导航回上一页或其他操作
            }}
            errorType="limit_exceeded"
            remainingCount={0}
            limitCount={0}
          />
        </div>
      );
    }

    return (
      <div className={`transcript-content-container ${
        isImmersiveMode 
          ? "bg-transparent border-none" 
          : "mt-1 bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50"
      }`}>
        {/* Hide header in immersive mode */}
        {!isImmersiveMode && (
          <div className="flex items-center justify-between mb-6 p-2 sm:p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-red-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">{t('transcript.title')}</h3>
            </div>
          </div>
        )}
                <EmptyState
            title={isTranscriptLoading ? t('transcript.generating') : t('transcript.generate')}
            description={isTranscriptLoading ? t('transcript.generatingDesc') : t('transcript.generateDesc')}
            icons={[<FileText key="p1" className="h-6 w-6" />, <MessageSquare key="p2" className="h-6 w-6" />, <Sparkles key="p3" className="h-6 w-6" />]}
            action={{ 
              label: isTranscriptLoading ? t('transcript.generating') : t('transcript.generate'), 
              icon: isTranscriptLoading ? <SpinnerIcon size="sm" /> : <Sparkles className="h-4 w-4" />, 
              onClick: () => {
                // console.log('🎯 EmptyState button clicked!');
                handleGenerateTranscript();
              },
              disabled: isTranscriptLoading || insufficientQuota
            }}
            usageFeedback={usageFeedbackState}
            quotaInfo={quotaInfo}
          />
        
        {/* Upgrade/Error Prompt Dialog - MOVED HERE to be included in early return */}
        {(() => {
          // console.log('🔥 About to render UpgradePromptDialog in empty state with:', { 
          //   showUpgradeDialog, 
          //   errorDetails 
          // });
          return (
            <UpgradePromptDialog
              isOpen={showUpgradeDialog}
              onClose={() => {
                // console.log('🔥 UpgradePromptDialog onClose called');
                setShowUpgradeDialog(false);
                setErrorDetails(null);
              }}
              errorType={errorDetails?.type || 'general'}
              remainingCount={errorDetails?.remaining}
              limitCount={errorDetails?.limit}
              errorMessage={errorDetails?.message}
            />
          );
        })()}
      </div>
    );
  }

  // 如果有内容但用户没有生成记录，不显示内容
  if (displayTranscript && displayTranscript.length > 0 && !shouldShowTranscript()) {
    // console.log('🔍 用户没有transcript生成记录，但数据库中存在transcript数据，不显示内容');
    return (
      <div className={`transcript-content-container ${
        isImmersiveMode 
          ? "bg-transparent border-none" 
          : "mt-1 bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50"
      }`}>
        {/* Hide header in immersive mode */}
        {!isImmersiveMode && (
          <div className="flex items-center justify-between mb-6 p-2 sm:p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-red-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">Transcript</h3>
            </div>
          </div>
        )}
        <EmptyState
            title={isTranscriptLoading ? t('transcript.generating') : t('transcript.generate')}
            description={isTranscriptLoading ? t('transcript.generatingDesc') : t('transcript.generateDesc')}
            icons={[<FileText key="p1" className="h-6 w-6" />, <MessageSquare key="p2" className="h-6 w-6" />, <Sparkles key="p3" className="h-6 w-6" />]}
            action={{ 
              label: isTranscriptLoading ? t('transcript.generating') : t('transcript.generate'), 
              icon: isTranscriptLoading ? <SpinnerIcon size="sm" /> : <Sparkles className="h-4 w-4" />, 
              onClick: () => {
                // console.log('🎯 EmptyState button clicked!');
                handleGenerateTranscript();
              },
              disabled: isTranscriptLoading || insufficientQuota
            }}
            usageFeedback={usageFeedbackState}
            quotaInfo={quotaInfo}
          />
        
        {/* Upgrade/Error Prompt Dialog */}
        <UpgradePromptDialog
          isOpen={showUpgradeDialog}
          onClose={() => {
            // console.log('🔥 UpgradePromptDialog onClose called');
            setShowUpgradeDialog(false);
            setErrorDetails(null);
          }}
          errorType={errorDetails?.type || 'general'}
          remainingCount={errorDetails?.remaining}
          limitCount={errorDetails?.limit}
          errorMessage={errorDetails?.message}
        />
      </div>
    );
  }

  return (
    <div className={`transcript-content-container ${
      isImmersiveMode 
        ? "bg-transparent border-none" 
        : "mt-1 bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50"
    }`}>
      {/* Hide header in immersive mode */}
      {!isImmersiveMode && (
        <div className="flex items-center justify-between mb-6 p-2 sm:p-4 border-b border-border/50">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-6 bg-red-500 rounded-full"></div>
            <h3 className="text-xl font-bold text-foreground">{t('transcript.title')}</h3>
          </div>
          <div className="text-sm text-muted-foreground">{displayTranscript.length} {t('transcript.segments')}</div>
        </div>
      )}

      {/* Transcript list with improved spacing */}
      
      <div
        className={`space-y-2 ${
          isImmersiveMode
            ? "p-0"
            : "px-2 sm:px-4 py-0 sm:py-4"
        } pb-[calc(env(safe-area-inset-bottom)+8rem)] sm:pb-24 lg:pb-12`}
      >
        {displayTranscript.map((entry, index) => {
          const subtitleId = generateSubtitleId(entry);
          const isActiveSubtitle = isImmersiveMode && activeSubtitleId === subtitleId;
          
          return (
            <motion.div 
              key={`transcript-${entry.timestamp[0]}-${entry.timestamp[1]}`} 
              className={`transition-all duration-200 ${
                isImmersiveMode && !isActiveSubtitle ? "opacity-50" : ""
              }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: isImmersiveMode && !isActiveSubtitle ? 0.5 : 1, 
                y: 0,
                scale: isImmersiveMode && isActiveSubtitle ? 1.05 : 1
              }}
              transition={{ 
                duration: 0.3, 
                delay: Math.min(index * 0.02, 0.5),
                ease: "easeOut"
              }}
            >
              <MemoizedGroupedTranscriptEntry
                userSettings={userSettings}
                group={entry}
                isActive={index === currentIndex}
                currentTime={youtubeTime}
                onSeek={handleSeek}
                player={ytPlayer}
                wordHighlightEnabled={wordHighlightEnabled}
                practiceMode={practiceMode}
                practiceModeType={practiceModeType}
                vocabularyTerms={stableVocabularyTerms}
                setIsPlaying={setIsPlaying}
                showTranslation={showTranslation}
                isWordCheckerReady={isWordCheckerReady}
                isVideoMinimized={isVideoMinimized}
                episodeId={episodeId}
                episodeTitle={episodeMetadata.title}
                episodeThumbnail={episodeMetadata.thumbnail}
                isImmersiveMode={isImmersiveMode}
                activeVocabularyTerms={activeVocabularyTerms}
                staticVocabularyTerms={staticVocabularyTerms}
              />
            </motion.div>
          );
        })}
      </div>

      {/* Upgrade/Error Prompt Dialog */}
      {(() => {
        // console.log('🔥 About to render UpgradePromptDialog with:', { 
        //   showUpgradeDialog, 
        //   errorDetails 
        // });
        return (
          <UpgradePromptDialog
            isOpen={showUpgradeDialog}
            onClose={() => {
              // console.log('🔥 UpgradePromptDialog onClose called');
              setShowUpgradeDialog(false);
              setErrorDetails(null);
            }}
            errorType={errorDetails?.type || 'general'}
            remainingCount={errorDetails?.remaining}
            limitCount={errorDetails?.limit}
            errorMessage={errorDetails?.message}
          />
        );
      })()}
    </div>
  );
} 
